import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAdminStats,
  fetchAdminReviews,
  adminDeleteReview,
  fetchAdminUsers,
  adminDeleteUser,
  fetchAdminActivity,
} from '../api';

const TABS = ['Overview', 'Kahanis', 'Users', 'Activity'];

const STATUS_COLOR = {
  offer: '#10b981',
  rejected: '#ef4444',
  ghosted: '#f97316',
  ongoing: '#8b5cf6',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AdminPage({ user }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, label }
  const [toast, setToast] = useState(null);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  // Load stats on mount
  useEffect(() => {
    fetchAdminStats().then(setStats).catch(() => {});
  }, []);

  const loadReviews = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const data = await fetchAdminReviews({ limit: 60, search: q });
      setReviews(data.reviews || []);
    } catch { /* handled */ }
    setLoading(false);
  }, []);

  const loadUsers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const data = await fetchAdminUsers({ limit: 60, search: q });
      setUsers(data.users || []);
    } catch { /* handled */ }
    setLoading(false);
  }, []);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminActivity({ limit: 60 });
      setActivity(data.activity || []);
    } catch { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'Kahanis') loadReviews(search);
    if (activeTab === 'Users') loadUsers(search);
    if (activeTab === 'Activity') loadActivity();
  }, [activeTab]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    if (activeTab !== 'Kahanis' && activeTab !== 'Users') return;
    const t = setTimeout(() => {
      if (activeTab === 'Kahanis') loadReviews(search);
      if (activeTab === 'Users') loadUsers(search);
    }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === 'review') {
        await adminDeleteReview(confirmDelete.id);
        setReviews(r => r.filter(x => x.id !== confirmDelete.id));
        if (stats) setStats(s => ({ ...s, totalReviews: s.totalReviews - 1 }));
        showToast('Kahani deleted');
      } else {
        await adminDeleteUser(confirmDelete.id);
        setUsers(u => u.filter(x => x.id !== confirmDelete.id));
        if (stats) setStats(s => ({ ...s, totalUsers: s.totalUsers - 1 }));
        showToast('User deleted');
      }
    } catch (err) {
      showToast(err.message || 'Delete failed', false);
    }
    setConfirmDelete(null);
  };

  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 60, textAlign: 'center', fontFamily: 'var(--sans)', color: 'var(--text3)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Admin Access Required</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', fontFamily: 'var(--sans)' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 80, right: 24, zIndex: 9999,
          background: toast.ok ? '#10b981' : '#ef4444',
          color: '#fff', padding: '10px 20px', borderRadius: 10,
          fontWeight: 700, fontSize: 13, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Confirm dialog */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998,
        }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 16, padding: 32, maxWidth: 420, width: '90%',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Confirm Delete
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24, lineHeight: 1.6 }}>
              Permanently delete <strong>{confirmDelete.label}</strong>?
              {confirmDelete.type === 'user' && (
                <> Their kahanis will be anonymized but preserved.</>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--surface2)', color: 'var(--text)', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: 'none',
                  background: '#ef4444', color: '#fff', fontSize: 13,
                  fontWeight: 700, cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
          Admin Dashboard
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>
          Logged in as <strong>{user.hkId}</strong> · Super Admin
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSearch(''); }}
            style={{
              padding: '8px 18px', borderRadius: 20, fontSize: 13, fontWeight: 700,
              border: '1px solid var(--border)', cursor: 'pointer',
              background: activeTab === tab ? 'var(--blue)' : 'var(--surface)',
              color: activeTab === tab ? '#fff' : 'var(--text2)',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'Overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: 'Total Users', value: stats?.totalUsers ?? '…', color: '#0ea5e9' },
            { label: 'Total Kahanis', value: stats?.totalReviews ?? '…', color: '#10b981' },
            { label: 'Total Notifications', value: stats?.totalNotifications ?? '…', color: '#8b5cf6' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 14, padding: '20px 24px',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* KAHANIS */}
      {activeTab === 'Kahanis' && (
        <>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by company, role, or HK ID..."
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
              border: '1px solid var(--border)', background: 'var(--surface)',
              color: 'var(--text)', marginBottom: 16, boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {reviews.map(r => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', background: 'var(--surface)',
                  border: '1px solid var(--border)', borderRadius: 10,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                      {r.role} at {r.companyName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span>by {r.authorHkId || r.authorName || 'Anonymous'}</span>
                      {r.status && (
                        <span style={{ color: STATUS_COLOR[r.status] || 'var(--text3)', fontWeight: 600 }}>
                          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        </span>
                      )}
                      <span>{timeAgo(r.createdAt)}</span>
                      <span>👍 {r.upvotes || 0}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmDelete({ type: 'review', id: r.id, label: `${r.role} at ${r.companyName}` })}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: 'none',
                      background: '#ef44441a', color: '#ef4444', fontSize: 12,
                      fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {reviews.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                  No kahanis found
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* USERS */}
      {activeTab === 'Users' && (
        <>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by username, display name, or HK ID..."
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
              border: '1px solid var(--border)', background: 'var(--surface)',
              color: 'var(--text)', marginBottom: 16, boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {users.map(u => (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', background: 'var(--surface)',
                  border: '1px solid var(--border)', borderRadius: 10,
                }}>
                  <img
                    src={u.avatarUrl}
                    alt=""
                    style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                      {u.displayName}
                      {u.isAdmin && (
                        <span style={{ marginLeft: 8, fontSize: 11, background: '#f97316', color: '#fff', padding: '1px 6px', borderRadius: 6, fontWeight: 700 }}>
                          ADMIN
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span>{u.hkId}</span>
                      <span>@{u.username}</span>
                      <span>Joined {timeAgo(u.createdAt)}</span>
                      <span>{u.totalKahanis || 0} kahanis</span>
                    </div>
                  </div>
                  {!u.isAdmin && u.id !== user.id && (
                    <button
                      onClick={() => setConfirmDelete({ type: 'user', id: u.id, label: `${u.displayName} (${u.hkId})` })}
                      style={{
                        padding: '6px 14px', borderRadius: 8, border: 'none',
                        background: '#ef44441a', color: '#ef4444', fontSize: 12,
                        fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
              {users.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                  No users found
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ACTIVITY */}
      {activeTab === 'Activity' && (
        loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activity.map(item => (
              <div key={item.id} style={{
                padding: '10px 14px', background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                    background: item._source === 'review' ? 'rgba(14,165,233,0.12)' : 'rgba(139,92,246,0.12)',
                    color: item._source === 'review' ? '#0ea5e9' : '#8b5cf6',
                  }}>
                    {item._source === 'review' ? '📝 Kahani' : `${item.type || 'notification'}`}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {timeAgo(item.createdAt)}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                  {item._source === 'review' ? (
                    <>
                      <strong>{item.authorHkId || item.authorName || 'Anonymous'}</strong>
                      {' posted '}
                      <strong>{item.role}</strong>
                      {' at '}
                      <strong>{item.companyName}</strong>
                    </>
                  ) : (
                    <>
                      <strong>{item.actorHkId || item.actorId || 'someone'}</strong>
                      {' → '}
                      <strong>{item.recipientHkId || item.recipientId || 'user'}</strong>
                      {' · '}
                      {item.companyName && <span>{item.companyName}</span>}
                    </>
                  )}
                </div>
              </div>
            ))}
            {activity.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                No activity found
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
